import React, { useEffect, useMemo, useState } from 'react';
import { Download, Image as ImageIcon, Plus, Save, Trash2, Type } from 'lucide-react';
import { albumBookService } from '../../services/albumBookService';

const BASE_PAGE_WIDTH = 1240;
const BASE_PAGE_HEIGHT = 1754;
const PX_PER_MM = BASE_PAGE_WIDTH / 210;
const PAGE_FORMATS_MM = {
  A3: { width: 297, height: 420 },
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  B4: { width: 250, height: 353 }
};

const PAGE_LAYOUTS = [
  {
    id: 'free-form',
    name: 'Free Form',
    slots: [],
    textAreas: []
  },
  {
    id: 'single-photo',
    name: 'Single Photo',
    slots: [{ id: 'photo1', x: 60, y: 90, w: 1120, h: 1250 }],
    textAreas: []
  },
  {
    id: 'two-photos',
    name: 'Two Photos',
    slots: [
      { id: 'photo1', x: 60, y: 90, w: 550, h: 1250 },
      { id: 'photo2', x: 630, y: 90, w: 550, h: 1250 }
    ],
    textAreas: []
  },
  {
    id: 'photo-text',
    name: 'Photo + Text',
    slots: [{ id: 'photo1', x: 60, y: 90, w: 730, h: 1570 }],
    textAreas: [{ id: 'body', x: 820, y: 130, w: 340, h: 1490, fontSize: 34 }]
  },
  {
    id: 'text-photo',
    name: 'Text + Photo',
    slots: [{ id: 'photo1', x: 60, y: 720, w: 1120, h: 940 }],
    textAreas: [{ id: 'body', x: 80, y: 110, w: 1080, h: 560, fontSize: 34 }]
  }
];

const defaultPage = () => ({
  title: 'Untitled Page',
  layoutId: PAGE_LAYOUTS[0].id,
  pageFormat: 'A4',
  orientation: 'portrait',
  slots: {},
  elements: [],
  textContent: {},
  backgroundColor: '#ffffff'
});

const getLayout = (layoutId) => PAGE_LAYOUTS.find((layout) => layout.id === layoutId) || PAGE_LAYOUTS[0];

const loadImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = url;
  });

const drawCoverImage = (ctx, image, x, y, w, h, options = {}) => {
  const ratio = Math.max(w / image.width, h / image.height) * (options.scale || 1);
  const drawWidth = image.width * ratio;
  const drawHeight = image.height * ratio;
  const normalizedX = Math.max(-1, Math.min(1, options.offsetX || 0));
  const normalizedY = Math.max(-1, Math.min(1, options.offsetY || 0));
  const posX = (normalizedX + 1) / 2;
  const posY = (normalizedY + 1) / 2;
  const drawX = x + (w - drawWidth) * posX;
  const drawY = y + (h - drawHeight) * posY;
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
};

const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = (text || '').split(/\s+/);
  const lines = [];
  let line = '';

  words.forEach((word) => {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  });

  if (line) lines.push(line);
  lines.forEach((lineText, index) => {
    ctx.fillText(lineText, x, y + index * lineHeight);
  });
};

const getPageMmDimensions = (page) => {
  const format = PAGE_FORMATS_MM[page?.pageFormat] || PAGE_FORMATS_MM.A4;
  if ((page?.orientation || 'portrait') === 'landscape') {
    return { width: format.height, height: format.width };
  }
  return { width: format.width, height: format.height };
};

const getPagePixelDimensions = (page) => {
  const mm = getPageMmDimensions(page);
  return {
    width: Math.round(mm.width * PX_PER_MM),
    height: Math.round(mm.height * PX_PER_MM)
  };
};

const scaleRectToPage = (rect, pageWidth, pageHeight) => ({
  x: Math.round((rect.x / BASE_PAGE_WIDTH) * pageWidth),
  y: Math.round((rect.y / BASE_PAGE_HEIGHT) * pageHeight),
  w: Math.round((rect.w / BASE_PAGE_WIDTH) * pageWidth),
  h: Math.round((rect.h / BASE_PAGE_HEIGHT) * pageHeight),
  fontSize: rect.fontSize ? Math.round((rect.fontSize / BASE_PAGE_HEIGHT) * pageHeight) : undefined
});

const renderPageToCanvas = async (page) => {
  const layout = getLayout(page.layoutId);
  const pagePixels = getPagePixelDimensions(page);
  const canvas = document.createElement('canvas');
  canvas.width = pagePixels.width;
  canvas.height = pagePixels.height;
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = page.backgroundColor || '#ffffff';
  ctx.fillRect(0, 0, pagePixels.width, pagePixels.height);

  if (page.layoutId === 'free-form') {
    const elements = page.elements || [];
    for (const element of elements) {
      const scaled = scaleRectToPage(element, pagePixels.width, pagePixels.height);
      if (element.type === 'image' && element.url) {
        ctx.save();
        ctx.fillStyle = '#f3f4f6';
        ctx.fillRect(scaled.x, scaled.y, scaled.w, scaled.h);
        ctx.strokeStyle = '#d1d5db';
        ctx.strokeRect(scaled.x, scaled.y, scaled.w, scaled.h);
        ctx.beginPath();
        ctx.rect(scaled.x, scaled.y, scaled.w, scaled.h);
        ctx.clip();
        try {
          const image = await loadImage(element.url);
          drawCoverImage(ctx, image, scaled.x, scaled.y, scaled.w, scaled.h, {
            offsetX: element.offsetX || 0,
            offsetY: element.offsetY || 0,
            scale: element.scale || 1
          });
        } catch (error) {
          // Keep placeholder
        }
        ctx.restore();
      }

      if (element.type === 'text' && element.text?.trim()) {
        const fontSize = scaled.fontSize || 30;
        ctx.fillStyle = element.color || '#111827';
        ctx.font = `${fontSize}px Arial`;
        wrapText(ctx, element.text, scaled.x, scaled.y + fontSize, scaled.w, fontSize * 1.3);
      }
    }
  } else {
    for (const slot of layout.slots) {
      const scaledSlot = scaleRectToPage(slot, pagePixels.width, pagePixels.height);
      ctx.save();
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(scaledSlot.x, scaledSlot.y, scaledSlot.w, scaledSlot.h);
      ctx.strokeStyle = '#d1d5db';
      ctx.strokeRect(scaledSlot.x, scaledSlot.y, scaledSlot.w, scaledSlot.h);
      ctx.beginPath();
      ctx.rect(scaledSlot.x, scaledSlot.y, scaledSlot.w, scaledSlot.h);
      ctx.clip();

      const slotData = page.slots?.[slot.id];
      if (slotData?.url) {
        try {
          const image = await loadImage(slotData.url);
          drawCoverImage(ctx, image, scaledSlot.x, scaledSlot.y, scaledSlot.w, scaledSlot.h, {
            offsetX: slotData.offsetX || 0,
            offsetY: slotData.offsetY || 0,
            scale: slotData.scale || 1
          });
        } catch (error) {
          // Leave placeholder when image fails
        }
      }
      ctx.restore();
    }

    layout.textAreas.forEach((textAreaDef) => {
      const textArea = scaleRectToPage(textAreaDef, pagePixels.width, pagePixels.height);
      const value = page.textContent?.[textAreaDef.id] || '';
      if (!value) return;
      ctx.fillStyle = '#111827';
      ctx.font = `${textArea.fontSize || 32}px Arial`;
      wrapText(
        ctx,
        value,
        textArea.x,
        textArea.y + (textArea.fontSize || 32),
        textArea.w,
        (textArea.fontSize || 32) * 1.3
      );
    });
  }

  return canvas;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

export const AlbumBookBuilder = ({ bookId, galleries = [], customStyling = {} }) => {
  const [pages, setPages] = useState([]);
  const [books, setBooks] = useState([]);
  const [selectedPageId, setSelectedPageId] = useState(null);
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [workingPage, setWorkingPage] = useState(defaultPage());
  const [bookTitle, setBookTitle] = useState('My Album Book');
  const [selectedBookPageIds, setSelectedBookPageIds] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [dragState, setDragState] = useState(null);

  const allMedia = useMemo(
    () => galleries.flatMap((gallery) => (gallery.mediaItems || []).map((item) => ({ ...item, galleryName: gallery.name, galleryId: gallery.id }))),
    [galleries]
  );

  const selectedLayout = useMemo(() => getLayout(workingPage.layoutId), [workingPage.layoutId]);
  const previewMm = useMemo(() => getPageMmDimensions(workingPage), [workingPage.pageFormat, workingPage.orientation]);
  const previewPixels = useMemo(() => getPagePixelDimensions(workingPage), [workingPage.pageFormat, workingPage.orientation]);
  const isFreeForm = workingPage.layoutId === 'free-form';

  const loadBuilderData = async () => {
    const [loadedPages, loadedBooks] = await Promise.all([
      albumBookService.getAlbumPages(bookId),
      albumBookService.getAlbumBooks(bookId)
    ]);
    setPages(loadedPages);
    setBooks(loadedBooks);

    if (loadedPages.length > 0 && !selectedPageId) {
      const firstPage = loadedPages[0];
      setSelectedPageId(firstPage.id);
      setWorkingPage({
        ...defaultPage(),
        ...firstPage
      });
    }
  };

  useEffect(() => {
    loadBuilderData();
  }, [bookId]);

  const selectPage = (page) => {
    setSelectedPageId(page.id);
    setWorkingPage({
      ...defaultPage(),
      ...page
    });
    setSelectedSlotId(null);
    setSelectedElementId(null);
  };

  const addNewPage = () => {
    setSelectedPageId(null);
    setWorkingPage(defaultPage());
    setSelectedSlotId(null);
    setSelectedElementId(null);
  };

  const savePage = async () => {
    setIsSaving(true);
    try {
      if (selectedPageId) {
        await albumBookService.updateAlbumPage(bookId, selectedPageId, workingPage);
      } else {
        const newId = await albumBookService.createAlbumPage(bookId, workingPage);
        setSelectedPageId(newId);
      }
      await loadBuilderData();
    } finally {
      setIsSaving(false);
    }
  };

  const deleteSelectedPage = async () => {
    if (!selectedPageId) return;
    if (!window.confirm('Delete this album page?')) return;
    await albumBookService.deleteAlbumPage(bookId, selectedPageId);
    setSelectedPageId(null);
    setWorkingPage(defaultPage());
    setSelectedBookPageIds((prev) => prev.filter((id) => id !== selectedPageId));
    await loadBuilderData();
  };

  const assignMediaToSlot = (mediaItem) => {
    if (isFreeForm) {
      const elementId = `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setWorkingPage((prev) => ({
        ...prev,
        elements: [
          ...(prev.elements || []),
          {
            id: elementId,
            type: 'image',
            x: 120,
            y: 140,
            w: 520,
            h: 420,
            url: mediaItem.url,
            mediaId: mediaItem.id,
            galleryId: mediaItem.galleryId,
            galleryName: mediaItem.galleryName,
            title: mediaItem.title || mediaItem.originalName || mediaItem.fileName || 'Photo',
            offsetX: 0,
            offsetY: 0,
            scale: 1
          }
        ]
      }));
      setSelectedElementId(elementId);
      return;
    }

    if (!selectedSlotId) return;
    setWorkingPage((prev) => ({
      ...prev,
      slots: {
        ...(prev.slots || {}),
        [selectedSlotId]: {
          url: mediaItem.url,
          mediaId: mediaItem.id,
          galleryId: mediaItem.galleryId,
          galleryName: mediaItem.galleryName,
          title: mediaItem.title || mediaItem.originalName || mediaItem.fileName || 'Photo',
          offsetX: 0,
          offsetY: 0,
          scale: 1
        }
      }
    }));
  };

  const addTextElement = () => {
    const elementId = `txt-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setWorkingPage((prev) => ({
      ...prev,
      elements: [
        ...(prev.elements || []),
        {
          id: elementId,
          type: 'text',
          x: 140,
          y: 620,
          w: 600,
          h: 220,
          fontSize: 34,
          text: '',
          color: '#111827'
        }
      ]
    }));
    setSelectedElementId(elementId);
  };

  const updateElement = (elementId, updates) => {
    setWorkingPage((prev) => ({
      ...prev,
      elements: (prev.elements || []).map((element) => (element.id === elementId ? { ...element, ...updates } : element))
    }));
  };

  const deleteSelectedElement = () => {
    if (!selectedElementId) return;
    setWorkingPage((prev) => ({
      ...prev,
      elements: (prev.elements || []).filter((element) => element.id !== selectedElementId)
    }));
    setSelectedElementId(null);
  };

  const handleFreeElementPointerDown = (event, element, mode = 'move') => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setSelectedElementId(element.id);

    setDragState({
      pointerId: event.pointerId,
      mode,
      elementId: element.id,
      startX: event.clientX,
      startY: event.clientY,
      startElement: { ...element },
      previewWidth: previewPixels.width,
      previewHeight: previewPixels.height
    });
  };

  const handleFreeElementPointerMove = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId || !dragState.elementId) return;
    const dxPreview = event.clientX - dragState.startX;
    const dyPreview = event.clientY - dragState.startY;
    const dxBase = (dxPreview / dragState.previewWidth) * BASE_PAGE_WIDTH;
    const dyBase = (dyPreview / dragState.previewHeight) * BASE_PAGE_HEIGHT;

    const start = dragState.startElement || {};
    if (dragState.mode === 'resize') {
      const nextW = Math.max(60, (start.w || 300) + dxBase);
      const nextH = Math.max(60, (start.h || 240) + dyBase);
      updateElement(dragState.elementId, { w: nextW, h: nextH });
      return;
    }

    const maxX = BASE_PAGE_WIDTH - (start.w || 0);
    const maxY = BASE_PAGE_HEIGHT - (start.h || 0);
    const nextX = Math.max(0, Math.min(maxX, (start.x || 0) + dxBase));
    const nextY = Math.max(0, Math.min(maxY, (start.y || 0) + dyBase));
    updateElement(dragState.elementId, { x: nextX, y: nextY });
  };

  const handleFreeElementPointerUp = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragState(null);
  };

  const handleSlotPointerDown = (event, slot) => {
    const slotData = workingPage.slots?.[slot.id];
    setSelectedSlotId(slot.id);
    if (!slotData?.url) return;

    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);

    setDragState({
      pointerId: event.pointerId,
      slotId: slot.id,
      startX: event.clientX,
      startY: event.clientY,
      startOffsetX: slotData.offsetX || 0,
      startOffsetY: slotData.offsetY || 0,
      slotWidth: slot.w,
      slotHeight: slot.h
    });
  };

  const handleSlotPointerMove = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const dx = event.clientX - dragState.startX;
    const dy = event.clientY - dragState.startY;
    const nextOffsetX = Math.max(-1, Math.min(1, dragState.startOffsetX - dx / (dragState.slotWidth / 2)));
    const nextOffsetY = Math.max(-1, Math.min(1, dragState.startOffsetY - dy / (dragState.slotHeight / 2)));

    setWorkingPage((prev) => ({
      ...prev,
      slots: {
        ...(prev.slots || {}),
        [dragState.slotId]: {
          ...(prev.slots?.[dragState.slotId] || {}),
          offsetX: nextOffsetX,
          offsetY: nextOffsetY
        }
      }
    }));
  };

  const handleSlotPointerUp = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    setDragState(null);
  };

  const exportPageImage = async (format = 'png') => {
    setIsExporting(true);
    try {
      const canvas = await renderPageToCanvas(workingPage);
      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
      const quality = format === 'jpeg' ? 0.92 : 1;
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, mimeType, quality));
      if (!blob) throw new Error('Failed to render image');
      downloadBlob(blob, `${(workingPage.title || 'album-page').replace(/\s+/g, '_')}.${format === 'jpeg' ? 'jpg' : 'png'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const createAlbumBook = async () => {
    if (!selectedBookPageIds.length) {
      alert('Select at least one page for the album book.');
      return;
    }
    await albumBookService.createAlbumBook(bookId, {
      title: bookTitle || 'Album Book',
      pageIds: selectedBookPageIds
    });
    setBookTitle('My Album Book');
    setSelectedBookPageIds([]);
    await loadBuilderData();
  };

  const exportAlbumBookPdf = async (book) => {
    setIsExporting(true);
    try {
      const { jsPDF } = await import('jspdf');
      const pageModels = book.pageIds
        .map((pageId) => pages.find((page) => page.id === pageId))
        .filter(Boolean);

      if (!pageModels.length) {
        alert('No valid pages in this album book.');
        return;
      }

      const firstPage = { ...defaultPage(), ...pageModels[0] };
      const firstMm = getPageMmDimensions(firstPage);
      const pdf = new jsPDF({
        unit: 'mm',
        format: [firstMm.width, firstMm.height],
        orientation: firstMm.width > firstMm.height ? 'landscape' : 'portrait'
      });

      for (let i = 0; i < pageModels.length; i += 1) {
        const model = { ...defaultPage(), ...pageModels[i] };
        const mm = getPageMmDimensions(model);
        const canvas = await renderPageToCanvas(model);
        const imageData = canvas.toDataURL('image/jpeg', 0.95);
        if (i > 0) {
          pdf.addPage([mm.width, mm.height], mm.width > mm.height ? 'landscape' : 'portrait');
        }
        pdf.addImage(imageData, 'JPEG', 0, 0, mm.width, mm.height);
      }

      pdf.save(`${(book.title || 'album-book').replace(/\s+/g, '_')}.pdf`);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold" style={{ color: customStyling.primaryColor || '#111827' }}>
          Album Page Builder
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={addNewPage} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50">
            <Plus className="h-4 w-4" />
            New Page
          </button>
          {isFreeForm && (
            <button onClick={addTextElement} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50">
              <Type className="h-4 w-4" />
              Add Caption
            </button>
          )}
          {isFreeForm && selectedElementId && (
            <button onClick={deleteSelectedElement} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
              Remove Element
            </button>
          )}
          <button onClick={savePage} disabled={isSaving} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60">
            <Save className="h-4 w-4" />
            Save Page
          </button>
          {selectedPageId && (
            <button onClick={deleteSelectedPage} className="inline-flex items-center gap-2 rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50">
              <Trash2 className="h-4 w-4" />
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="xl:col-span-2">
          <div className="mb-2 text-sm font-medium text-gray-700">Saved Pages</div>
          <div className="max-h-[520px] space-y-2 overflow-auto">
            {pages.map((page) => (
              <button
                key={page.id}
                onClick={() => selectPage(page)}
                className={`w-full rounded-lg border px-3 py-2 text-left text-sm ${
                  selectedPageId === page.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                {page.title || 'Untitled Page'}
              </button>
            ))}
          </div>
        </div>

        <div className="xl:col-span-6">
          <div className="mb-3 grid grid-cols-1 gap-2 md:grid-cols-4">
            <input
              value={workingPage.title || ''}
              onChange={(event) => setWorkingPage((prev) => ({ ...prev, title: event.target.value }))}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm md:col-span-2"
              placeholder="Page title"
            />
            <select
              value={workingPage.layoutId}
              onChange={(event) => {
                const nextLayoutId = event.target.value;
                setWorkingPage((prev) => ({ ...prev, layoutId: nextLayoutId }));
                setSelectedSlotId(null);
                setSelectedElementId(null);
              }}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
            >
              {PAGE_LAYOUTS.map((layout) => (
                <option key={layout.id} value={layout.id}>
                  {layout.name}
                </option>
              ))}
            </select>
            <div className="grid grid-cols-2 gap-2 md:col-span-4">
              <select
                value={workingPage.pageFormat || 'A4'}
                onChange={(event) => setWorkingPage((prev) => ({ ...prev, pageFormat: event.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                {Object.keys(PAGE_FORMATS_MM).map((formatKey) => (
                  <option key={formatKey} value={formatKey}>
                    {formatKey}
                  </option>
                ))}
              </select>
              <select
                value={workingPage.orientation || 'portrait'}
                onChange={(event) => setWorkingPage((prev) => ({ ...prev, orientation: event.target.value }))}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="portrait">Portrait</option>
                <option value="landscape">Landscape</option>
              </select>
            </div>
          </div>

          <div className="mb-3 rounded-lg border border-gray-200 bg-gray-100 p-3">
            <div
              className="mx-auto max-h-[620px] max-w-[520px] overflow-hidden rounded-md border border-gray-300 bg-white p-3"
              style={{ aspectRatio: `${previewMm.width} / ${previewMm.height}` }}
            >
              <div className="relative h-full w-full">
                {isFreeForm ? (
                  (workingPage.elements || []).map((element) => {
                    const scaled = scaleRectToPage(element, previewPixels.width, previewPixels.height);
                    const isSelected = selectedElementId === element.id;
                    return (
                      <div
                        key={element.id}
                        className={`absolute overflow-hidden rounded border-2 ${isSelected ? 'border-blue-500' : 'border-dashed border-gray-300'}`}
                        style={{
                          left: `${(scaled.x / previewPixels.width) * 100}%`,
                          top: `${(scaled.y / previewPixels.height) * 100}%`,
                          width: `${(scaled.w / previewPixels.width) * 100}%`,
                          height: `${(scaled.h / previewPixels.height) * 100}%`,
                          touchAction: 'none'
                        }}
                        onPointerDown={(event) => handleFreeElementPointerDown(event, element, 'move')}
                        onPointerMove={handleFreeElementPointerMove}
                        onPointerUp={handleFreeElementPointerUp}
                        onPointerCancel={handleFreeElementPointerUp}
                      >
                        {element.type === 'image' ? (
                          <img
                            src={element.url}
                            alt={element.title || 'image'}
                            className="h-full w-full object-cover"
                            draggable={false}
                          />
                        ) : (
                          <textarea
                            value={element.text || ''}
                            onChange={(event) => updateElement(element.id, { text: event.target.value })}
                            onPointerDown={(event) => event.stopPropagation()}
                            className="h-full w-full resize-none border-none bg-white/85 p-2 text-xs outline-none"
                            placeholder="Caption"
                          />
                        )}
                        {isSelected && (
                          <div
                            className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize rounded-tl bg-blue-500"
                            onPointerDown={(event) => handleFreeElementPointerDown(event, element, 'resize')}
                          />
                        )}
                      </div>
                    );
                  })
                ) : (
                  <>
                    {selectedLayout.slots.map((slot) => {
                      const scaledSlot = {
                        ...scaleRectToPage(slot, previewPixels.width, previewPixels.height),
                        id: slot.id
                      };
                      const slotData = workingPage.slots?.[slot.id];
                      const objectPosition = `${((slotData?.offsetX || 0) + 1) * 50}% ${((slotData?.offsetY || 0) + 1) * 50}%`;
                      return (
                        <button
                          key={slot.id}
                          onPointerDown={(event) => handleSlotPointerDown(event, scaledSlot)}
                          onPointerMove={handleSlotPointerMove}
                          onPointerUp={handleSlotPointerUp}
                          onPointerCancel={handleSlotPointerUp}
                          className={`absolute overflow-hidden rounded border-2 ${
                            selectedSlotId === slot.id ? 'border-blue-500' : 'border-dashed border-gray-300'
                          }`}
                          style={{
                            left: `${(scaledSlot.x / previewPixels.width) * 100}%`,
                            top: `${(scaledSlot.y / previewPixels.height) * 100}%`,
                            width: `${(scaledSlot.w / previewPixels.width) * 100}%`,
                            height: `${(scaledSlot.h / previewPixels.height) * 100}%`,
                            touchAction: 'none',
                            cursor: slotData?.url ? (dragState?.slotId === slot.id ? 'grabbing' : 'grab') : 'pointer'
                          }}
                        >
                          {slotData?.url ? (
                            <img
                              src={slotData.url}
                              alt={slotData.title || slot.id}
                              className="h-full w-full object-cover"
                              style={{
                                objectPosition,
                                transform: `scale(${slotData.scale || 1})`,
                                transformOrigin: 'center center'
                              }}
                              draggable={false}
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-xs text-gray-500">
                              <ImageIcon className="mr-1 h-4 w-4" />
                              {slot.id}
                            </div>
                          )}
                        </button>
                      );
                    })}

                    {selectedLayout.textAreas.map((textAreaDef) => {
                      const textArea = scaleRectToPage(textAreaDef, previewPixels.width, previewPixels.height);
                      return (
                        <textarea
                          key={textAreaDef.id}
                          value={workingPage.textContent?.[textAreaDef.id] || ''}
                          onChange={(event) =>
                            setWorkingPage((prev) => ({
                              ...prev,
                              textContent: {
                                ...(prev.textContent || {}),
                                [textAreaDef.id]: event.target.value
                              }
                            }))
                          }
                          className="absolute resize-none rounded border border-gray-300 bg-white/85 p-2 text-xs"
                          style={{
                            left: `${(textArea.x / previewPixels.width) * 100}%`,
                            top: `${(textArea.y / previewPixels.height) * 100}%`,
                            width: `${(textArea.w / previewPixels.width) * 100}%`,
                            height: `${(textArea.h / previewPixels.height) * 100}%`
                          }}
                          placeholder={`Text: ${textAreaDef.id}`}
                        />
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={() => exportPageImage('png')} disabled={isExporting} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50">
              <Download className="h-4 w-4" />
              Export PNG
            </button>
            <button onClick={() => exportPageImage('jpeg')} disabled={isExporting} className="inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-gray-50">
              <Download className="h-4 w-4" />
              Export JPEG
            </button>
          </div>
        </div>

        <div className="xl:col-span-4">
          <div className="mb-2 text-sm font-medium text-gray-700">
            {isFreeForm ? 'Click any image to add it to this page' : selectedSlotId ? `Select image for ${selectedSlotId}` : 'Select a slot on page'}
          </div>
          <div className="mb-4 max-h-[240px] overflow-auto rounded-lg border border-gray-200 p-2">
            <div className="grid grid-cols-3 gap-2">
              {allMedia.filter((item) => item.type === 'image').map((item) => (
                <button key={`${item.galleryId}-${item.id}`} onClick={() => assignMediaToSlot(item)} className="overflow-hidden rounded border border-gray-200">
                  <img src={item.url} alt={item.title || 'media'} className="h-20 w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 p-3">
            <div className="mb-2 text-sm font-semibold text-gray-800">Album Books</div>
            <input
              value={bookTitle}
              onChange={(event) => setBookTitle(event.target.value)}
              className="mb-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              placeholder="Album book title"
            />
            <select
              multiple
              value={selectedBookPageIds}
              onChange={(event) =>
                setSelectedBookPageIds(Array.from(event.target.selectedOptions).map((option) => option.value))
              }
              className="mb-2 h-36 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
            >
              {pages.map((page) => (
                <option key={page.id} value={page.id}>
                  {page.title || 'Untitled Page'}
                </option>
              ))}
            </select>
            <button onClick={createAlbumBook} className="mb-3 w-full rounded-lg bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700">
              Save Album Book
            </button>

            <div className="space-y-2">
              {books.map((book) => (
                <div key={book.id} className="rounded-md border border-gray-200 p-2">
                  <div className="text-sm font-medium text-gray-800">{book.title || 'Album Book'}</div>
                  <div className="mb-2 text-xs text-gray-500">{(book.pageIds || []).length} pages</div>
                  <button
                    onClick={() => exportAlbumBookPdf(book)}
                    disabled={isExporting}
                    className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm hover:bg-gray-50"
                  >
                    Download PDF
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlbumBookBuilder;
